// src/lib/redirectByRole.ts
import type { NavigateFunction } from "react-router-dom";
import type { EMTProfile, EmployerProfile } from "../types/firestore";
import type { UserProfile } from "../types/user";

type Profile = EMTProfile | EmployerProfile | UserProfile | null;

const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

/**
 * Remembers where an unauthenticated visitor was actually trying to go (e.g. a credentialing
 * checklist link from a transactional email) so redirectByRole can send them there instead of
 * always landing on the role's default dashboard after login. Called from ProtectedRoute when
 * it bounces a signed-out visitor away from a protected route.
 */
export function savePostLoginRedirect(path: string): void {
  if (!path || path === '/' || path.startsWith('/p/')) return;
  try {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
  } catch {
    // sessionStorage unavailable (private mode, etc.) — falls back to default dashboard redirect
  }
}

/**
 * Clears the stored post-login destination, but only if `arrivedAt` is exactly the path that was
 * remembered — landing on some other protected route (e.g. the default dashboard) leaves it in
 * place, since redirectByRole may still be in the middle of applying it (see resolvePostLoginRedirect).
 */
export function clearPostLoginRedirectIfArrived(arrivedAt: string): void {
  try {
    if (sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY) === arrivedAt) {
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    }
  } catch {
    // ignore
  }
}

/**
 * Non-destructive: returns the stored destination if one exists and its namespace matches this
 * role, else null. Deliberately does not clear the key on read — redirectByRole is invoked from
 * multiple, sometimes-racing call sites (the login form's own handler and RootRoute's effect) for
 * a single login, and both should agree on the same target. The key is cleared once the visitor
 * actually lands on that route (see ProtectedRoute), not on read.
 *
 * Exported because a BRAND-NEW account never reaches the branch below that reads it: redirectByRole
 * only restores the destination once onboarding is complete, and by then EMTRegStep1 has already
 * navigated on its own. Step 1 asks for the destination directly (see EMTRegStep1.finishStep1) so a
 * stranger who arrived from an employer's careers page lands on that job's application, not on a
 * signup step and not on the dashboard.
 */
export function resolvePostLoginRedirect(role: 'emt' | 'employer'): string | null {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  } catch {
    return null;
  }
  if (!stored) return null;
  const isEmployerPath = stored.startsWith('/employer');
  if ((role === 'employer') !== isEmployerPath) return null;
  return stored;
}

/**
 * What a remembered destination IS, in the visitor's words — so a signup screen that is about to
 * send them there says so, instead of promising "Step 2" and then going somewhere else.
 * Null when there is no destination (the ordinary signup, which does continue to step 2).
 */
export function describePostLoginDestination(path: string | null | undefined): string | null {
  if (!path) return null;
  if (/^\/jobs\/[^/]+\/apply(?:[/?#]|$)/.test(path)) return 'your application';
  if (/^\/jobs\/[^/]+(?:[/?#]|$)/.test(path)) return 'the job you were looking at';
  if (/^\/class\/[^/]+/.test(path)) return 'your class';
  if (/^\/applications\/schedule\//.test(path) || /^\/book\//.test(path)) return 'your interview invitation';
  if (/^\/credentialing/.test(path)) return 'your credentialing checklist';
  return 'the page you were opening';
}

/**
 * Centralized redirect logic based on user role and onboarding status.
 * 
 * CRITICAL RULES:
 * - EMTs NEVER go to /employer/company-setup
 * - Employers ONLY go to /employer/company-setup if they don't have orgId
 * - No profile / no role yet goes to the role choice (/sso/role-selection), never a guess
 * - An unrecognised role string defaults to EMT registration (never employer)
 */
export function redirectByRole(
  profile: Profile,
  navigate: NavigateFunction,
  options?: { currentPath?: string }
) {
  const role = profile?.role;
  const currentPath = options?.currentPath;

  // Log for debugging
  if (import.meta.env.DEV) console.log('[redirectByRole] Called with profile:', { 
    hasProfile: !!profile, 
    role, 
    currentPath,
    orgId: (profile as any)?.orgId,
    emtOnboarding: (profile as any)?.onboarding?.emt,
    employerOnboarding: (profile as any)?.onboarding?.employer,
    fullOnboarding: (profile as any)?.onboarding
  });

  // No profile or no role → they signed in but never picked EMT / employer. Send them to the role
  // choice (the same page a profileless sign-in already lands on), never guess a role for them.
  if (!profile || !role) {
    // Don't yank users who are already completing registration / role selection
    if (
      currentPath?.startsWith('/register/') ||
      currentPath === '/sso/role-selection'
    ) {
      if (import.meta.env.DEV) console.log('[redirectByRole] No role yet but already in onboarding — skipping redirect');
      return;
    }
    if (import.meta.env.DEV) console.log('[redirectByRole] No profile or role detected, redirecting to the role choice');
    navigate("/sso/role-selection", { replace: true });
    return;
  }

  // ===== EMT BRANCH =====
  // EMT logic ONLY checks EMT-specific fields - NEVER looks at orgId or employer onboarding
  if (role === "emt") {
    const emtProfile = profile as EMTProfile;
    const emtOnboarding = emtProfile?.onboarding?.emt;
    const emtCompleted = emtOnboarding?.completed === true;
    const emtStep = Number(emtOnboarding?.step ?? 1);

    if (import.meta.env.DEV) console.log('[redirectByRole] EMT detected:', {
      emtCompleted,
      emtStep,
      onboardingData: emtOnboarding
    });

    if (!emtCompleted) {
      // Already inside /register/* — do NOT bounce between steps.
      // Step transitions are owned by EMTRegStep1/2 (avoids stale-profile traps).
      if (currentPath?.startsWith('/register/')) {
        if (import.meta.env.DEV) console.log('[redirectByRole] EMT incomplete but already on register path — skipping:', currentPath);
        return;
      }
      // Account step (step 1: name + location) not finished → Step 1. Steps 2–4 are optional
      // profile completion on a usable account, so they are never a redirect target here.
      const targetPath = "/register/step1";
      if (currentPath === targetPath) return;
      if (import.meta.env.DEV) console.log('[redirectByRole] EMT incomplete onboarding, redirecting to:', targetPath);
      navigate(targetPath, { replace: true });
    } else {
      // EMT complete → restore the deep-link destination they actually came for, if any
      const deepLinkTarget = resolvePostLoginRedirect('emt');
      if (deepLinkTarget && deepLinkTarget !== currentPath) {
        if (import.meta.env.DEV) console.log('[redirectByRole] EMT onboarding complete, restoring deep-link destination:', deepLinkTarget);
        navigate(deepLinkTarget, { replace: true });
        return;
      }
      // EMT complete → EMT dashboard
      if (currentPath === '/dashboard' || currentPath?.startsWith('/dashboard')) return;
      if (import.meta.env.DEV) console.log('[redirectByRole] EMT onboarding complete, redirecting to /dashboard');
      navigate("/dashboard", { replace: true });
    }
    return; // CRITICAL: Return immediately - EMTs NEVER fall through to employer logic
  }

  // ===== INSTITUTE BRANCH =====
  // Medical/Training Institute accounts have a single home: their listing dashboard.
  if (role === "institute") {
    if (currentPath?.startsWith('/institute/')) return;
    navigate("/institute/dashboard", { replace: true });
    return;
  }

  // ===== EMPLOYER BRANCH =====
  // Employer logic ONLY runs when role === "employer" - checks orgId OR employer onboarding
  if (role === "employer") {
    const employer = profile as EmployerProfile;
    const orgId = employer.orgId;
    // Check for employer onboarding status (may not be in type definition yet)
    const employerOnboarding = (employer as any)?.onboarding?.employer;
    const employerCompleted = employerOnboarding?.completed === true || !!orgId;

    if (import.meta.env.DEV) console.log('[redirectByRole] Employer detected:', {
      orgId,
      employerCompleted,
      onboardingData: employerOnboarding
    });

    if (!employerCompleted) {
      if (import.meta.env.DEV) console.log('[redirectByRole] Employer incomplete onboarding, redirecting to /employer/company-setup');
      navigate("/employer/company-setup", { replace: true });
    } else {
      // Employer complete → restore the deep-link destination they actually came for, if any
      const deepLinkTarget = resolvePostLoginRedirect('employer');
      if (deepLinkTarget && deepLinkTarget !== currentPath) {
        if (import.meta.env.DEV) console.log('[redirectByRole] Employer onboarding complete, restoring deep-link destination:', deepLinkTarget);
        navigate(deepLinkTarget, { replace: true });
        return;
      }
      if (import.meta.env.DEV) console.log('[redirectByRole] Employer onboarding complete, redirecting to /employer/dashboard');
      navigate("/employer/dashboard", { replace: true });
    }
    return; // CRITICAL: Return immediately
  }

  // ===== FALLBACK =====
  // Unknown role → safest default is EMT registration (NEVER employer onboarding)
  console.warn('[redirectByRole] Unknown role detected:', role, '- defaulting to EMT registration (NOT employer)');
  navigate("/register/step1", { replace: true });
}

